import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import type { RootAppState } from 'store/configureStore'

export type QuotationExchangeInvalidation = {
  partyId: string
  kind: 'rfq' | 'quotation'
  itemId: string
  projectId?: string
}

type RfqCountResponse = { received: number; parsed: number }
type QuotationCountResponse = { received: number }

export class QuotationExchangeCountClient extends InternalAPI {
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(`/api/parties/${partyId}`, onRequestChange)
  }

  incomingRfqs(): Promise<RfqCountResponse> {
    return this.GetAsync({
      relativePath: '/request-for-quotations/incoming/counts',
      id: 'CountActionableIncomingRfqs',
    })
  }

  receivedProjectQuotations(projectId: string): Promise<QuotationCountResponse> {
    return this.GetAsync({
      relativePath: `/quotations/incoming/counts?projectId=${projectId}`,
      id: `CountReceivedProjectQuotations-${projectId}`,
    })
  }
}

export class QuotationExchangeCountController extends BaseController<QuotationExchangeCountClient> {
  constructor(partyId: string) {
    super((onRequestChange) =>
      new QuotationExchangeCountClient(partyId, onRequestChange),
    )
  }

  async actionableIncomingRfqCount() {
    try {
      return (await this.api.incomingRfqs()).parsed
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  async receivedProjectQuotationCount(projectId: string) {
    try {
      return (await this.api.receivedProjectQuotations(projectId)).received
    } catch (error) {
      throw this.HandleError(error)
    }
  }
}

export const fetchActionableIncomingRfqCount = createAsyncThunk<
  { partyId: string; count: number },
  string,
  { state: RootAppState }
>('quotationExchange/fetchActionableIncomingRfqCount', async (partyId) => ({
  partyId,
  count: await new QuotationExchangeCountController(
    partyId,
  ).actionableIncomingRfqCount(),
}))

export const fetchReceivedProjectQuotationCount = createAsyncThunk<
  { projectId: string; count: number },
  { partyId: string; projectId: string },
  { state: RootAppState }
>('quotationExchange/fetchReceivedProjectQuotationCount', async (args) => ({
  projectId: args.projectId,
  count: await new QuotationExchangeCountController(
    args.partyId,
  ).receivedProjectQuotationCount(args.projectId),
}))

type QuotationExchangeState = {
  partyId?: string
  actionableIncomingRfqCount: number
  receivedQuotationCounts: Record<string, number>
  invalidation?: QuotationExchangeInvalidation
  invalidationSequence: number
}

const initialState: QuotationExchangeState = {
  actionableIncomingRfqCount: 0,
  receivedQuotationCounts: {},
  invalidationSequence: 0,
}

const quotationExchangeSlice = createSlice({
  name: 'quotationExchange',
  initialState,
  reducers: {
    partyChanged(state, action: PayloadAction<string | undefined>) {
      state.partyId = action.payload
      state.actionableIncomingRfqCount = 0
      state.receivedQuotationCounts = {}
      state.invalidation = undefined
      state.invalidationSequence = 0
    },
    invalidated(state, action: PayloadAction<QuotationExchangeInvalidation>) {
      state.invalidation = action.payload
      state.invalidationSequence += 1
    },
    clear(state) {
      state.partyId = undefined
      state.actionableIncomingRfqCount = 0
      state.receivedQuotationCounts = {}
      state.invalidation = undefined
      state.invalidationSequence = 0
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchActionableIncomingRfqCount.fulfilled, (state, action) => {
      if (state.partyId === action.payload.partyId) {
        state.actionableIncomingRfqCount = action.payload.count
      }
    })
    builder.addCase(
      fetchReceivedProjectQuotationCount.fulfilled,
      (state, action) => {
        if (state.partyId === action.meta.arg.partyId) {
          state.receivedQuotationCounts[action.payload.projectId] =
            action.payload.count
        }
      },
    )
  },
})

export const quotationExchangeActions = quotationExchangeSlice.actions
export default quotationExchangeSlice.reducer
